import type { JudgmentProfile } from '../../types/decisionIntelligence';
import { buildJudgmentSummary, TIER_META } from '../../lib/judgmentScoringEngine';

interface Props {
  profile: JudgmentProfile;
  showEmail?: boolean;
  compact?: boolean;
}

function ScoreBar({ value, label }: { value: number | null; label: string }) {
  if (value === null) return null;
  const pct = Math.round(value * 100);
  const color =
    pct >= 80 ? 'bg-[var(--green)]' :
    pct >= 60 ? 'bg-[var(--slate)]'    :
    pct >= 40 ? 'bg-[var(--mustard)]'   :
                'bg-[var(--coral)]';

  return (
    <div>
      <div className="flex justify-between text-[10px] text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-0.5">
        <span>{label}</span>
        <span className="tabular-nums">{pct}%</span>
      </div>
      <div className="h-1.5 bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function JudgmentProfileCard({ profile, showEmail = true, compact = false }: Props) {
  const summary = buildJudgmentSummary(profile);
  const meta    = TIER_META[summary.tier];

  const displayName = profile.email
    ? profile.email.split('@')[0].replace(/[._-]/g, ' ')
    : profile.userId.slice(0, 8);

  const domainLabel = profile.domain.charAt(0).toUpperCase() + profile.domain.slice(1);

  return (
    <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] p-4 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate capitalize">{displayName}</p>
          {showEmail && profile.email && (
            <p className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-sec)] truncate">{profile.email}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.color}`}>
            {meta.label}
          </span>
          <span className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-sec)] bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] px-2 py-0.5 rounded-full">{domainLabel}</span>
        </div>
      </div>

      {/* Score bars */}
      {!compact && (
        <div className="space-y-2">
          <ScoreBar value={profile.calibrationScore}          label="Calibration"             />
          <ScoreBar value={profile.confidenceAppropriateness} label="Confidence fit"          />
          <ScoreBar value={profile.proceedAccuracy}           label="Proceed accuracy"        />
        </div>
      )}

      {/* Sample size */}
      <div className="flex gap-3 text-[10px] text-[var(--text-ter)] dark:text-[var(--text-sec)]">
        <span><span className="text-[var(--text-sec)] dark:text-[var(--text-pri)] font-medium tabular-nums">{profile.signalCount}</span> signals</span>
        <span><span className="text-[var(--text-sec)] dark:text-[var(--text-pri)] font-medium tabular-nums">{profile.resolvedCount}</span> resolved</span>
      </div>

      {/* Insights */}
      {!compact && (summary.strengths.length > 0 || summary.cautions.length > 0) && (
        <div className="space-y-1 pt-1 border-t border-[var(--rule)] dark:border-[var(--rule)]">
          {summary.strengths.slice(0, 2).map((s, i) => (
            <p key={i} className="text-[10px] text-[var(--green)] dark:text-[var(--text-ter)] flex gap-1.5">
              <span className="shrink-0">✓</span>{s}
            </p>
          ))}
          {summary.cautions.slice(0, 1).map((c, i) => (
            <p key={i} className="text-[10px] text-[var(--mustard)] dark:text-[var(--mustard)] flex gap-1.5">
              <span className="shrink-0">△</span>{c}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
